import { useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Flex, notification } from "antd";
import clsx from "clsx";
import { journalsServiceApi } from "@/entities/api";
import { User, userQueries } from "@/entities/user";
import { NOTIFICATION_MESSAGES } from "@/shared/utils";
import type { components } from "@entities/journal";
import { useCoordinator } from "@shared/lib/hooks";
import { extractSignXml, JournalCryptographyWrapper } from "@shared/ui";
import { CancelSignModal } from "./cancel-sign-modal";

type Signer = components["schemas"]["SignerInfo"];

type Developer = "developer";

type Representatives =
  | Developer
  | "buildingContractor"
  | "technicalCustomer"
  | "designerSupervisions"
  | "otherDevelopers";

type PendingSignProps<T extends Representatives> = {
  children: ReactNode;
  signer?: Signer;
  onSuccess?: () => void;
  developerType?: T extends Developer ? string : never;
  representativeType: Representatives;
};

export const PendingSign = <T extends Representatives>({
  children,
  signer,
  representativeType,
  developerType,
  onSuccess,
}: PendingSignProps<T>) => {
  const [signingString, setSigningString] = useState<string>("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const { titleId } = useCoordinator();
  const { data: user  } = useQuery(userQueries.currentUser());

  const { mutateAsync: getSigningXml, isPending } = useMutation({
    mutationFn: (data: any) =>
      journalsServiceApi.post(`/journals/title/${titleId}/sign/`, data),
  });

  const { mutateAsync: sendSignedXml } = useMutation({
    mutationFn: (data: any) =>
      journalsServiceApi.post(`/journals/title/${titleId}/sign/done`, data),
  });

  const setupSingingString = (onOpen: () => void) => {
    getSigningXml({
      [representativeType]: {
        representativeId: signer?.representativeId,
        constructionControlId: signer?.constructionControlId,
        developerType,
      },
    })
      .then((res) => {
        const xml =
          typeof res.data === "string" ? res.data : res.data?.content ?? "";
        setSigningString(xml);
        onOpen();
      })
      .catch((err) => {
        notification.error({
          message: NOTIFICATION_MESSAGES.SIGN_ERROR, // "Произошла ошибка"
          description: err.response.data.errorDescription || "Не известно",
          placement: "topRight",
        });
      });
  };

  const withSignedString = (data: string, _proxyId?: string | null, mchdId?: string) => {
    try {
      const signatureXml = extractSignXml(data);
      sendSignedXml({
        signer: {
          representativeId: signer?.representativeId,
          constructionControlId: signer?.constructionControlId,
          developerType,
        },
        signXml: signatureXml,
        ...(mchdId ? { mchdId } : {}),
      })
        .then(() => {
          onSuccess?.();
        })
        .catch((err) => {
          notification.error({
            message: NOTIFICATION_MESSAGES.SIGN_SEND_ERROR, // "Произошла ошибка при отправлении подписи"
            description: err.response.data.errorDescription || "Не известно",
            placement: "topRight",
          });
        });
    } catch {
      notification.error({
        message: NOTIFICATION_MESSAGES.XML_SIGN_PROCESSING_ERROR, // "Ошибка при обработке подписи"
        description: NOTIFICATION_MESSAGES.XML_SIGN_EXTRACT_FAILED_DESC, // "Не удалось извлечь данные подписи из XML"
        placement: "topRight",
      });
      return null;
    }
  };

  return (
    <div className="table-block">
      <div
        className={clsx("table-container table-container-signature", {
          [`table-container-signature-pending`]: "pending",
        })}
      >
        {children}
        <div className="represented-container pending">
          <Flex gap={16}>
            <JournalCryptographyWrapper
              currentUser={user as User}
              signContent={signingString}
              onSuccess={withSignedString}
            >
              {({ onOpen }) => (
                <Button
                  type="primary"
                  onClick={() => setupSingingString(onOpen)}
                  loading={isPending}
                >
                  Подписать
                </Button>
              )}
            </JournalCryptographyWrapper>

            <Button
              variant="filled"
              color="default"
              onClick={() => setCancelModalOpen(true)}
            >
              Отказаться
            </Button>
            <CancelSignModal
              titleId={titleId}
              representativeId={signer?.representativeId ?? ""}
              constructionControlId={signer?.constructionControlId ?? ""}
              open={cancelModalOpen}
              onCancel={() => setCancelModalOpen(false)}
            />
          </Flex>
        </div>
      </div>
    </div>
  );
};
